interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class ServerCache {
  private cache: Map<string, CacheItem<any>> = new Map();
  
  // 获取缓存数据，如果不存在或已过期则执行获取函数
  async get<T>(key: string, fetcher: () => Promise<T>, ttl: number = 3600, timeoutMs: number = 0): Promise<T> {
    const now = Date.now();
    const cacheKey = key;
    const cachedItem = this.cache.get(cacheKey);
    
    // 检查缓存
    if (cachedItem) {
      // 如果缓存未过期
      if (cachedItem.expiresAt > now) {
        return cachedItem.data;
      }
    }
    
    let data: T;
    try {
      const request = fetcher();
      data = timeoutMs > 0
        ? await Promise.race([
            request,
            new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`cache fetch timeout after ${timeoutMs}ms`)), timeoutMs))
          ])
        : await request;
    } catch (error) {
      if (cachedItem) {
        return cachedItem.data;
      }
      throw error;
    }
    
    // 存入缓存
    this.cache.set(cacheKey, {
      data,
      timestamp: now,
      expiresAt: now + (ttl * 1000) // 转换为毫秒
    });
    
    return data;
  }
  
  // 手动清除指定缓存
  invalidate(key: string): boolean {
    return this.cache.delete(key);
  }
  
  // 清除所有缓存或匹配特定前缀的缓存
  invalidateAll(prefix?: string): void {
    if (!prefix) {
      this.cache.clear();
      return;
    }
    
    // 删除匹配前缀的缓存
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }
}

// 创建单例
export const serverCache = new ServerCache();
